import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Download, List, CalendarDays, X, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { oapi, odownload } from '@/lib/officialsApi';
import { GameRow } from './GameRow';
import { AssignDialog } from './AssignDialog';
import { BulkAssignDialog, CopyCrewDialog, ManualConfirmDialog, AuditDialog } from './GameDialogs';
import { OfficialsCalendar } from './OfficialsCalendar';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { useOverrideFlow } from './ConfirmWarningsDialog';

const STATUS_FILTERS = [['', 'All games'], ['none', 'No officials'], ['partial', 'Partially staffed'], ['full', 'Fully staffed'],
  ['pending', 'Pending confirmations'], ['declined', 'Declined'], ['conflict', 'Conflicts'], ['cancelled', 'Cancelled']];

const WHEN = [['upcoming', 'Upcoming'], ['past', 'Past'], ['all', 'All']];
const WHEN_LABEL = { upcoming: 'Upcoming games', past: 'Past games', all: 'All games' };

const Segmented = ({ options, value, onChange, testPrefix }) => (
  <div className="flex rounded-lg border p-0.5" data-testid={`${testPrefix}-toggle`}>
    {options.map(([k, l, Icon]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testPrefix}-${k}`}
        className={`px-2.5 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition-colors ${value === k ? 'bg-[#00247D] text-white' : 'text-slate-600 hover:text-slate-900'}`}>
        {Icon && <Icon className="w-3.5 h-3.5" />}{l}
      </button>
    ))}
  </div>
);

const SummaryChip = ({ label, value, active, onClick, tone, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded-xl border px-3 py-2 text-left transition-colors ${active ? 'border-[#00247D] bg-[#00247D] text-white' : 'bg-white hover:border-[#00247D]/40'}`}>
    <div className={`text-xl font-black leading-none ${active ? 'text-white' : tone}`} style={{ fontFamily: 'Barlow Condensed' }}>{value}</div>
    <div className={`text-[11px] font-semibold mt-0.5 ${active ? 'text-white/80' : 'text-slate-500'}`}>{label}</div>
  </button>
);

const monthRange = (m) => {
  const pad = (n) => String(n).padStart(2, '0');
  const last = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
  return [`${m.getFullYear()}-${pad(m.getMonth() + 1)}-01`, `${m.getFullYear()}-${pad(m.getMonth() + 1)}-${pad(last)}`];
};

export const GamesTab = ({ orgId, roles, officials, initialEventId, canCancel }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('list');
  const [when, setWhen] = useState('upcoming');
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [f, setF] = useState({ event_id: initialEventId || '', status: '', venue: '', official_id: '', from_date: '', to_date: '' });
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [assign, setAssign] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [confirmA, setConfirmA] = useState(null);
  const [historyA, setHistoryA] = useState(null);
  const [cancelGame, setCancelGame] = useState(null);

  const query = useMemo(() => {
    const q = { ...f, when };
    if (view === 'calendar') { const [a, b] = monthRange(month); q.from_date = a; q.to_date = b; q.include_past = '1'; q.when = 'all'; q.status = ''; }
    return q;
  }, [f, view, month, when]);

  const load = useCallback(async () => {
    try { setData(await oapi('/officials/games', { orgId, query })); } catch (e) { toast.error(e.message); }
  }, [orgId, query]);
  useEffect(() => { load(); }, [load]);

  const games = data?.games || [];
  const keyOf = (g) => `${g.event_id}:${g.match_id}`;
  const selectedGames = games.filter(g => selected.has(keyOf(g)));
  const toggle = (k) => setSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v === 'all' ? '' : v }));

  const { run: runGuarded, dialog: guardDialog } = useOverrideFlow();
  const removeAssignments = async (ids, label) => {
    try {
      const r = await runGuarded((_, ack) => oapi('/officials/assignments/remove', { method: 'POST', orgId, body: { assignment_ids: ids, notify: true, acknowledge_finance: ack } }));
      if (!r) return;
      toast.success(`${label || `${r.removed} assignment(s)`} removed · official notified`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const resend = async (ids) => {
    try { const r = await oapi('/officials/assignments/notify', { method: 'POST', orgId, body: { assignment_ids: ids } }); toast.success(`${r.sent} email(s) sent`); }
    catch (e) { toast.error(e.message); }
  };
  const onAction = (type, a, g) => {
    if (type === 'confirm') setConfirmA(a);
    if (type === 'history') setHistoryA(a);
    if (type === 'resend') resend([a.id]);
    if (type === 'remove') removeAssignments([a.id], a.official_name);
    if (type === 'replace') setAssign({ game: g, roleKey: a.role_key, replace: a });
  };
  const setGameStatus = async (g, status) => {
    const token = localStorage.getItem('session_token');
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${g.event_id}/schedule/${g.match_id}/status`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ status }) });
    if (!res.ok) { toast.error((await res.json().catch(() => ({}))).detail || 'Could not update game'); return; }
    toast.success(status === 'cancelled' ? 'Game cancelled · assigned officials notified' : 'Game restored');
    setCancelGame(null);
    load();
  };
  const onGameAction = (type, g) => {
    if (type === 'copy') setCopySource(g);
    if (type === 'notify') resend(g.assignments.filter(a => a.status === 'pending').map(a => a.id));
    if (type === 'open') navigate(`/schedule/${g.event_id}`);
    if (type === 'cancel') setCancelGame(g);
    if (type === 'restore') setGameStatus(g, 'scheduled');
  };
  const exportAs = (fmt) => odownload('/officials/export', { ...query, org_id: orgId, format: fmt }, `officials_schedule.${fmt}`).catch(e => toast.error(e.message));
  const s = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="officials-games-tab">
      {view === 'list' && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <SummaryChip label={WHEN_LABEL[when]} value={s.total ?? '–'} active={!f.status} onClick={() => set('status', '')} tone="text-[#00247D]" testId="summary-total" />
          <SummaryChip label="No officials" value={s.none ?? '–'} active={f.status === 'none'} onClick={() => set('status', 'none')} tone="text-red-600" testId="summary-none" />
          <SummaryChip label="Partially staffed" value={s.partial ?? '–'} active={f.status === 'partial'} onClick={() => set('status', 'partial')} tone="text-amber-600" testId="summary-partial" />
          <SummaryChip label="Fully staffed" value={s.full ?? '–'} active={f.status === 'full'} onClick={() => set('status', 'full')} tone="text-emerald-600" testId="summary-full" />
          <SummaryChip label="Pending confirmations" value={s.pending ?? '–'} active={f.status === 'pending'} onClick={() => set('status', 'pending')} tone="text-amber-600" testId="summary-pending" />
          <SummaryChip label="Declined" value={s.declined ?? '–'} active={f.status === 'declined'} onClick={() => set('status', 'declined')} tone="text-red-600" testId="summary-declined" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 bg-white border rounded-xl p-2.5">
        <Segmented options={[['list', 'Table', List], ['calendar', 'Calendar', CalendarDays]]} value={view} onChange={setView} testPrefix="games-view" />
        {view === 'list' && <Segmented options={WHEN} value={when} onChange={(w) => { setWhen(w); setSelected(new Set()); }} testPrefix="games-when" />}
        <Select value={f.event_id || 'all'} onValueChange={v => set('event_id', v)}>
          <SelectTrigger className="w-full sm:w-44 h-9" data-testid="filter-competition"><SelectValue placeholder="Competition" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All competitions</SelectItem>{(data?.competitions || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        {view === 'list' && <>
          <Select value={f.status || 'all'} onValueChange={v => set('status', v)}>
            <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-44 h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_FILTERS.map(([v, l]) => <SelectItem key={v || 'all'} value={v || 'all'}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={f.from_date} onChange={e => set('from_date', e.target.value)} className="w-[calc(50%-0.25rem)] sm:w-36 h-9" data-testid="filter-from" />
          <Input type="date" value={f.to_date} onChange={e => set('to_date', e.target.value)} className="w-[calc(50%-0.25rem)] sm:w-36 h-9" data-testid="filter-to" />
        </>}
        <Select value={f.venue || 'all'} onValueChange={v => set('venue', v)}>
          <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-36 h-9" data-testid="filter-venue"><SelectValue placeholder="Venue" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All venues</SelectItem>{(data?.venues || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.official_id || 'all'} onValueChange={v => set('official_id', v)}>
          <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-40 h-9" data-testid="filter-official"><SelectValue placeholder="Official" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All officials</SelectItem>{(officials || []).map(o => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
        </Select>
        {(f.from_date || f.to_date || f.venue || f.official_id || f.status || f.event_id) && (
          <Button variant="ghost" size="sm" onClick={() => setF({ event_id: '', status: '', venue: '', official_id: '', from_date: '', to_date: '' })} data-testid="filter-clear"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="ml-auto h-9" data-testid="export-menu-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportAs('xlsx')} data-testid="export-xlsx">Excel (.xlsx)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportAs('csv')} data-testid="export-csv">CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportAs('pdf')} data-testid="export-pdf">PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {view === 'list' && when === 'past' && games.length > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="past-games-note">
          <History className="w-4 h-4 shrink-0 mt-px" />
          <span>Fixing a past game? Officials you add are recorded as <b>Accepted</b> without any emails, and removed officials aren't emailed either. Fees update in Payments (the Verify queue).</span>
        </div>
      )}
      {selected.size > 0 && view === 'list' && (
        <div className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-xl bg-[#00247D] text-white px-3 py-2 shadow-lg" data-testid="bulk-bar">
          <span className="text-sm font-semibold mr-2">{selected.size} selected</span>
          <Button size="sm" className="bg-amber-400 text-[#00247D] hover:bg-amber-300" onClick={() => setBulkOpen(true)} data-testid="bulk-assign-btn">Assign official</Button>
          {when !== 'past' && <Button size="sm" variant="secondary" disabled={!selectedGames.some(g => !g.is_past && g.assignments.some(a => a.status === 'pending'))}
            onClick={() => resend(selectedGames.filter(g => !g.is_past).flatMap(g => g.assignments.filter(a => a.status === 'pending').map(a => a.id)))} data-testid="bulk-notify-btn">Send emails</Button>}
          <Button size="sm" variant="secondary" onClick={() => removeAssignments(selectedGames.flatMap(g => g.assignments.filter(a => ['pending', 'accepted'].includes(a.status)).map(a => a.id)))}
            data-testid="bulk-remove-btn">Remove officials</Button>
          <button type="button" onClick={() => setSelected(new Set())} className="ml-auto text-white/70 hover:text-white text-sm">Clear</button>
        </div>
      )}

      {!data ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        : view === 'calendar' ? (
          <OfficialsCalendar games={games} month={month} onMonth={setMonth}
            onPickDay={(d) => { setF(prev => ({ ...prev, from_date: d, to_date: d, status: '' })); setView('list'); }} />
        ) : games.length === 0 ? (
          <div className="bg-white border rounded-xl py-14 text-center text-slate-500" data-testid="games-empty">
            {when === 'past' ? 'No past games match these filters.' : 'No games match these filters. Games from your schedules appear here automatically.'}
          </div>
        ) : (
          <div className="bg-white border rounded-xl overflow-hidden" data-testid="games-list">
            <div className="flex items-center gap-3 px-3 sm:px-4 py-2 border-b bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
              <input type="checkbox" className="accent-[#00247D]" checked={selected.size === games.length} data-testid="games-select-all"
                onChange={(e) => setSelected(e.target.checked ? new Set(games.map(keyOf)) : new Set())} />
              <span>{games.length} game{games.length === 1 ? '' : 's'}</span>
            </div>
            {games.map(g => (
              <GameRow key={keyOf(g)} g={g} selected={selected.has(keyOf(g))} onSelect={toggle} canCancel={canCancel} currency={data.currency}
                onAssign={(game, roleKey) => setAssign({ game, roleKey })} onAction={onAction} onGameAction={onGameAction} />
            ))}
          </div>
        )}

      <AssignDialog open={!!assign} orgId={orgId} game={assign?.game} roleKey={assign?.roleKey} replace={assign?.replace} roles={roles}
        onClose={() => setAssign(null)} onDone={() => { setAssign(null); load(); }} />
      <BulkAssignDialog open={bulkOpen} orgId={orgId} games={selectedGames} roles={roles} officials={officials}
        onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); setSelected(new Set()); load(); }} />
      <CopyCrewDialog open={!!copySource} orgId={orgId} source={copySource} games={games} preselected={[...selected]}
        onClose={() => setCopySource(null)} onDone={() => { setCopySource(null); load(); }} />
      <ManualConfirmDialog open={!!confirmA} orgId={orgId} assignment={confirmA} onClose={() => setConfirmA(null)} onDone={() => { setConfirmA(null); load(); }} />
      <AuditDialog open={!!historyA} orgId={orgId} assignment={historyA} onClose={() => setHistoryA(null)} />
      <ConfirmDeleteDialog open={!!cancelGame} title="Cancel this game?" itemName={cancelGame && `${cancelGame.team_a_name} vs ${cancelGame.team_b_name}`}
        description="will show as Cancelled on your schedule and public pages. Assigned officials are emailed and their reminders stop. You can restore it later."
        confirmLabel="Cancel Game" onClose={() => setCancelGame(null)} onConfirm={() => setGameStatus(cancelGame, 'cancelled')} testId="cancel-game" />
      {guardDialog}
    </div>
  );
};

export default GamesTab;
